import { useCallback, useEffect, useRef } from "react";
import { useWindowDimensions } from "react-native";
import { Redirect, router, Stack, usePathname, useSegments } from "expo-router";
import { useAtom, useAtomValue, useSetAtom } from "jotai/react";

import { AppSidebar } from "@/blocks/AppSidebar";
import { BackButton } from "@/blocks/BackButton";
import { HeaderMenuDropdown } from "@/blocks/HeaderMenuDropdown";
import { LoadingScreen } from "@/blocks/LoadingScreen";
import {
  SessionTimeoutDialog,
  SessionTimeoutDialogHandleType,
} from "@/blocks/SessionTimeoutDialog";
import { VnBox } from "@/components/VnBox";
import { CurrentPatientContextProvider } from "@/context/CurrentPatientProvider";
import { useSignOutMutation } from "@/data/mutations/authMutations";
import { useCurrentPatientQuery } from "@/data/queries/patientsQueries.ts";
import {
  MFA_ENROLMENT_PATHNAME,
  MFA_ENROLMENT_ROUTE,
  MOBILE_BREAKPOINT,
} from "@/domain/constants";
import { sidebarItems } from "@/domain/sidebar";
import { useAppTheme } from "@/hooks/useAppTheme";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { checkHasEnrolledMfa, checkIsSsoUser } from "@/services/firebase";
import {
  hasCompletedRegistrationAtom,
  isManualLogoutAtom,
  isSessionExpiredAtom,
  needsToCreatePasswordAtom,
} from "@/stores/atoms";
import { useAuthEmail, useIsAuthenticated } from "@/stores/authStore";
import { checkIsUsernameAuthEmail } from "@/utils/usernameAuth";

export default function AuthLayout() {
  const isAuthenticated = useIsAuthenticated();
  const authEmail = useAuthEmail();
  const { isWeb } = usePlatformOS();
  const { width } = useWindowDimensions();
  const { data: patient } = useCurrentPatientQuery({
    isEnabled: isAuthenticated === true,
  });
  const pathname = usePathname();
  const segments = useSegments();

  const { resolvedColors } = useAppTheme();

  const { mutate: signOut } = useSignOutMutation();
  const setIsSessionExpired = useSetAtom(isSessionExpiredAtom);
  const setIsManualLogout = useSetAtom(isManualLogoutAtom);
  const sessionTimeoutRef = useRef<SessionTimeoutDialogHandleType>(null);

  const handleSessionTimeout = useCallback(() => {
    setIsSessionExpired(true);
    signOut();
  }, [signOut, setIsSessionExpired]);

  const handleManualLogoutFromDialog = useCallback(() => {
    setIsManualLogout(true);
    signOut();
  }, [signOut, setIsManualLogout]);

  // Automatically detect if we're in the (modal) group
  const isInModalGroup = (segments as string[]).includes("(modal)");

  // Don't show sidebar on registration routes even if isRegistered becomes true
  const isOnRegistrationRoute = pathname.startsWith("/registration");

  // Reactive check: sidebar shows on web when width >= 768px
  const isDesktopWeb = isWeb && width >= MOBILE_BREAKPOINT;

  const [hasCompletedRegistration, setHasCompletedRegistration] = useAtom(
    hasCompletedRegistrationAtom,
  );
  const shouldCreatePassword = useAtomValue(needsToCreatePasswordAtom);

  // A username account is refused patient data until its session completes the
  // SMS challenge, so it must reach enrolment without waiting for that data.
  const shouldEnrolMfa =
    isAuthenticated === true &&
    checkIsUsernameAuthEmail(authEmail) &&
    !checkHasEnrolledMfa() &&
    !checkIsSsoUser();

  useEffect(() => {
    if (isAuthenticated !== true || isOnRegistrationRoute) {
      return;
    }

    if (shouldCreatePassword || !shouldEnrolMfa) {
      return;
    }

    router.replace(MFA_ENROLMENT_ROUTE);
  }, [
    isAuthenticated,
    isOnRegistrationRoute,
    shouldEnrolMfa,
    shouldCreatePassword,
  ]);

  useEffect(() => {
    if (!patient) {
      return;
    }

    // Run only when not in registration to avoid kicking the user
    if (!isOnRegistrationRoute) {
      // If registration is already marked complete, don't override with potentially
      // stale patient data (e.g. patient query hasn't refetched yet after profile step)
      if (hasCompletedRegistration) {
        return;
      }

      // Set registration status based on completion flags
      setHasCompletedRegistration(
        Boolean(patient.hasCompletedProfile) &&
          Boolean(patient.hasSignedConsentDocument),
      );

      // Redirect appropriately based on registration states
      if (shouldCreatePassword) {
        if (pathname !== "/registration/create-password") {
          router.replace("/(authed)/registration/create-password");
        }
        return;
      }

      // Handled by the effect above.
      if (shouldEnrolMfa) {
        return;
      }

      if (!checkHasEnrolledMfa() && !checkIsSsoUser()) {
        if (pathname !== MFA_ENROLMENT_PATHNAME) {
          router.replace(MFA_ENROLMENT_ROUTE);
        }
        return;
      }

      if (patient.hasCompletedProfile && !patient.hasSignedConsentDocument) {
        if (pathname !== "/registration/consent-form") {
          router.replace("/(authed)/registration/consent-form");
        }
      } else if (!patient.hasCompletedProfile) {
        if (pathname !== "/registration/profile") {
          router.replace("/(authed)/registration/profile");
        }
      }
    }
  }, [
    patient,
    isOnRegistrationRoute,
    hasCompletedRegistration,
    setHasCompletedRegistration,
    shouldEnrolMfa,
    shouldCreatePassword,
    pathname,
  ]);

  const isSessionExpired = useAtomValue(isSessionExpiredAtom);
  const isManualLogout = useAtomValue(isManualLogoutAtom);

  if (isAuthenticated === false) {
    if (isManualLogout) {
      return <Redirect href="/(unauthed)/auth" />;
    }
    return (
      <Redirect
        href={
          isSessionExpired
            ? "/(unauthed)/session-expired"
            : "/(unauthed)/onboarding"
        }
      />
    );
  }

  if (isAuthenticated === undefined) {
    return <LoadingScreen />;
  }

  const content = (
    <CurrentPatientContextProvider>
      <VnBox className="bg-background min-h-0 flex-1">
        <Stack
          screenOptions={{
            headerShown: true,
            headerLeft: () => <BackButton className="mx-4 h-8" />,
            headerTitle: "",
          }}>
          <Stack.Protected guard={hasCompletedRegistration && !shouldEnrolMfa}>
            <Stack.Screen name="index" options={{ headerShown: false }} />
            <Stack.Screen name="account" options={{ headerShown: false }} />
            <Stack.Screen name="household/[householdMemberId]/index" />

            <Stack.Screen
              name="household/[householdMemberId]/profile-information"
              options={{ headerShown: false }}
            />
            <Stack.Screen name="application/[applicationType]" />
            <Stack.Screen name="renewals/medicaid" />
            <Stack.Screen name="work-requirements/index" />
            <Stack.Screen name="work-requirements/[id]" />

            {/* Modal group - renders as full screen modal without sidebar */}
            <Stack.Screen
              name="(modal)"
              options={{
                headerShown: false,
                presentation: "fullScreenModal",
              }}
            />
          </Stack.Protected>

          {/* Registration Screens */}
          <Stack.Protected guard={!hasCompletedRegistration || shouldEnrolMfa}>
            <Stack.Screen
              name="registration/profile"
              options={{
                headerLeft: () => null,
                headerBackVisible: false,
                headerRight: () => <HeaderMenuDropdown className="mr-5" />,
                headerStyle: { backgroundColor: resolvedColors.sidebar },
              }}
            />
            <Stack.Screen
              name="registration/create-password"
              options={{
                headerLeft: undefined,
                headerRight: () => <HeaderMenuDropdown className="mr-5" />,
                headerStyle: { backgroundColor: resolvedColors.sidebar },
              }}
            />
            <Stack.Screen
              name="registration/mfa-phone"
              options={{
                headerRight: () => <HeaderMenuDropdown className="mr-5" />,
                headerStyle: { backgroundColor: resolvedColors.sidebar },
                headerLeft: undefined,
              }}
            />
            <Stack.Screen
              name="registration/mfa-verify"
              options={{
                headerRight: () => <HeaderMenuDropdown className="mr-5" />,
                headerStyle: { backgroundColor: resolvedColors.sidebar },
              }}
            />
            <Stack.Screen
              name="registration/consent-form"
              options={{
                headerRight: () => <HeaderMenuDropdown className="mr-5" />,
                headerStyle: { backgroundColor: resolvedColors.sidebar },
              }}
            />
            <Stack.Screen
              name="registration/check-coverage"
              options={{
                headerRight: () => <HeaderMenuDropdown className="mr-5" />,
                headerStyle: { backgroundColor: resolvedColors.sidebar },
              }}
            />
          </Stack.Protected>
        </Stack>
      </VnBox>
    </CurrentPatientContextProvider>
  );

  // Only show sidebar on desktop web (≥768px) and not during registration or modal flows
  if (
    isDesktopWeb &&
    hasCompletedRegistration &&
    !isOnRegistrationRoute &&
    !isInModalGroup
  ) {
    return (
      <VnBox
        className="web:pl-64 min-h-screen w-full flex-1 flex-row"
        onStartShouldSetResponderCapture={() => {
          sessionTimeoutRef.current?.onUserActivity();
          return false;
        }}>
        <AppSidebar items={sidebarItems} />
        {content}
        <SessionTimeoutDialog
          ref={sessionTimeoutRef}
          onTimeout={handleSessionTimeout}
          onManualLogout={handleManualLogoutFromDialog}
        />
      </VnBox>
    );
  }

  return (
    <VnBox
      className="flex-1"
      onStartShouldSetResponderCapture={() => {
        sessionTimeoutRef.current?.onUserActivity();
        return false;
      }}>
      {content}
      <SessionTimeoutDialog
        ref={sessionTimeoutRef}
        onTimeout={handleSessionTimeout}
        onManualLogout={handleManualLogoutFromDialog}
      />
    </VnBox>
  );
}
